import React, { useState, useEffect } from 'react';
import {
  FileText, Download, Plus, Trash2, RefreshCw,
  Calendar, ShieldCheck, ChevronDown, ChevronUp,
  FileSpreadsheet, FileBadge, Info,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function fmtDateShort(d) {
  if (!d) return '—';
  return new Date(d + 'T12:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
}
function today() { return new Date().toISOString().slice(0, 10); }
function firstOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

const MONTHS = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre',
];

export default function AuditReports() {
  const [snapshots, setSnapshots] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [creating,  setCreating]  = useState(false);
  const [dlLoading, setDlLoading] = useState({});
  const [showNew,   setShowNew]   = useState(false);
  const [preview,   setPreview]   = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error,     setError]     = useState('');
  const [deleting,  setDeleting]  = useState(null);

  // Formulario nuevo snapshot
  const [form, setForm] = useState({
    period_label: '',
    period_from:  firstOfMonth(),
    period_to:    today(),
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = () => {
    setLoading(true);
    fetch(`${API_URL}/audit/snapshots`, { headers })
      .then(r => r.json())
      .then(d => setSnapshots(Array.isArray(d) ? d : []))
      .catch(() => setSnapshots([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Auto-generar label del período
  useEffect(() => {
    if (!form.period_from || !form.period_to) return;
    const d = new Date(form.period_from + 'T12:00:00');
    const t = new Date(form.period_to   + 'T12:00:00');
    let label;
    if (d.getMonth() === t.getMonth() && d.getFullYear() === t.getFullYear()) {
      label = `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
    } else {
      label = `${MONTHS[d.getMonth()]} ${d.getFullYear()} — ${MONTHS[t.getMonth()]} ${t.getFullYear()}`;
    }
    setForm(f => ({ ...f, period_label: label }));
  }, [form.period_from, form.period_to]);

  const loadPreview = async () => {
    if (!form.period_from || !form.period_to) return;
    setPreviewLoading(true);
    try {
      const r = await fetch(`${API_URL}/audit/preview?from=${form.period_from}&to=${form.period_to}`, { headers });
      const d = await r.json();
      setPreview(d);
    } catch { setPreview(null); }
    finally { setPreviewLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/audit/snapshot`, {
        method: 'POST', headers,
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setShowNew(false);
      setPreview(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const download = async (id, format, label) => {
    const key = `${id}-${format}`;
    setDlLoading(l => ({ ...l, [key]: true }));
    try {
      const r = await fetch(`${API_URL}/audit/${id}/${format}`, { headers });
      if (!r.ok) throw new Error(`Error ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `auditoria-${label.replace(/\s/g, '-').toLowerCase()}.${format}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setError(err.message);
    } finally {
      setDlLoading(l => ({ ...l, [key]: false }));
    }
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await fetch(`${API_URL}/audit/snapshots/${id}`, { method: 'DELETE', headers });
      load();
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div style={{ maxWidth: 860 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <FileBadge size={22} style={{ opacity: 0.7 }} />
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Reportes de Auditoría</h1>
          </div>
          <p style={{ margin: 0, color: 'var(--ink-2)', fontSize: 13 }}>
            Snapshots del inventario para auditorías ISO 27001, SOC 2 o informes mensuales de cliente.
          </p>
        </div>
        <button
          onClick={() => { setShowNew(s => !s); setPreview(null); setError(''); }}
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 18px', background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
          <Plus size={15} /> Nuevo reporte
        </button>
      </div>

      {error && (
        <div style={{ background: 'var(--red-dim)', border: '1px solid var(--red-dim)', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: 'var(--red)', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* ── Formulario nuevo snapshot ─────────────────────────────────────── */}
      {showNew && (
        <div className="card" style={{ padding: 24, marginBottom: 24, border: '2px solid #e0e7ff' }}>
          <h2 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Calendar size={16} style={{ color: 'var(--accent)' }} /> Configurar nuevo snapshot
          </h2>

          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 5 }}>Desde</label>
                <input type="date" required value={form.period_from} max={form.period_to || today()}
                  onChange={e => setForm(f => ({ ...f, period_from: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 5 }}>Hasta</label>
                <input type="date" required value={form.period_to} min={form.period_from} max={today()}
                  onChange={e => setForm(f => ({ ...f, period_to: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 5 }}>
                Nombre del período <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>(auto-generado, podés editarlo)</span>
              </label>
              <input type="text" required value={form.period_label}
                onChange={e => setForm(f => ({ ...f, period_label: e.target.value }))}
                placeholder="ej: Enero 2026"
                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
            </div>

            {/* Preview */}
            <div style={{ marginBottom: 20 }}>
              <button type="button" onClick={loadPreview} disabled={previewLoading}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 7, fontSize: 12, fontWeight: 500, cursor: 'pointer', color: 'var(--ink-2)' }}>
                {previewLoading ? <RefreshCw size={12} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Info size={12} />}
                Vista previa del contenido
              </button>

              {preview && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
                  {[
                    { label: 'Activos',          value: preview.total_assets },
                    { label: 'Parches OK',        value: preview.patch_pct + '%' },
                    { label: 'Incidentes abiertos', value: preview.incidents_open },
                    { label: 'Usuarios',          value: preview.users_total },
                  ].map(s => (
                    <div key={s.label} style={{ flex: 1, minWidth: 90, background: 'var(--bg-subtle)', border: '1px solid #e0e7ff', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--accent)' }}>{s.value}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-2)', marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" disabled={creating}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: creating ? 'not-allowed' : 'pointer', opacity: creating ? 0.7 : 1 }}>
                {creating ? <><RefreshCw size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> Generando...</> : <><ShieldCheck size={14} /> Crear snapshot</>}
              </button>
              <button type="button" onClick={() => { setShowNew(false); setPreview(null); }}
                style={{ padding: '10px 16px', background: 'none', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: 'var(--ink-2)' }}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Listado de snapshots ──────────────────────────────────────────── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--ink-3)' }}>
          <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} />
          <div>Cargando reportes...</div>
        </div>
      ) : snapshots.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center' }}>
          <FileBadge size={40} style={{ color: 'var(--border-2)', marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Sin reportes de auditoría</div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 20 }}>
            Creá el primer snapshot para empezar. El sistema también genera uno automáticamente el último día de cada mes.
          </div>
          <button onClick={() => setShowNew(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px', background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            <Plus size={14} /> Crear primer reporte
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {snapshots.map(snap => (
            <SnapshotCard
              key={snap.id}
              snap={snap}
              dlLoading={dlLoading}
              deleting={deleting}
              onDownload={(fmt) => download(snap.id, fmt, snap.period_label)}
              onDelete={() => handleDelete(snap.id)}
            />
          ))}
        </div>
      )}

      {/* Info cron */}
      <div style={{ marginTop: 20, padding: '12px 16px', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--ink-2)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Calendar size={13} />
        El sistema genera automáticamente un snapshot el último día de cada mes a las 23:45 (hora Argentina).
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function SnapshotCard({ snap, dlLoading, deleting, onDownload, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const assets = snap.total_assets ?? '—';
  const incOpen = snap.incidents_open ?? '—';

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '18px 24px' }}>
        {/* Ícono */}
        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <ShieldCheck size={22} style={{ color: 'var(--accent)' }} />
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>{snap.period_label}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
            <Calendar size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />
            {snap.period_from ? fmtDateShort(snap.period_from) : '—'} → {snap.period_to ? fmtDateShort(snap.period_to) : '—'}
            <span style={{ margin: '0 8px', color: 'var(--border)' }}>|</span>
            Capturado: {snap.captured_at ? fmtDateShort(snap.captured_at) : '—'}
            {snap.captured_by_name && <span style={{ color: 'var(--ink-3)' }}> por {snap.captured_by_name}</span>}
          </div>
        </div>

        {/* Badges */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink)' }}>{assets}</div>
            <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>activos</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: incOpen > 0 ? 'var(--amber)' : 'var(--green)' }}>{incOpen}</div>
            <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>inc. abiertos</div>
          </div>
        </div>

        <button onClick={() => setExpanded(e => !e)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', padding: 4 }}>
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
      </div>

      {/* Acciones expandidas */}
      {expanded && (
        <div style={{ borderTop: '1px solid var(--bg)', padding: '16px 24px', background: 'var(--bg-subtle)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* PDF */}
          <button onClick={() => onDownload('pdf')} disabled={dlLoading[`${snap.id}-pdf`]}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: dlLoading[`${snap.id}-pdf`] ? 'var(--bg)' : 'var(--red-dim)', color: dlLoading[`${snap.id}-pdf`] ? 'var(--ink-3)' : 'var(--red)', border: '1px solid var(--red-dim)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: dlLoading[`${snap.id}-pdf`] ? 'not-allowed' : 'pointer' }}>
            {dlLoading[`${snap.id}-pdf`]
              ? <><RefreshCw size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> Generando...</>
              : <><FileText size={14} /> PDF</>}
          </button>

          {/* Excel */}
          <button onClick={() => onDownload('xlsx')} disabled={dlLoading[`${snap.id}-xlsx`]}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: dlLoading[`${snap.id}-xlsx`] ? 'var(--bg)' : 'var(--green-dim)', color: dlLoading[`${snap.id}-xlsx`] ? 'var(--ink-3)' : 'var(--green)', border: '1px solid var(--green-dim)', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: dlLoading[`${snap.id}-xlsx`] ? 'not-allowed' : 'pointer' }}>
            {dlLoading[`${snap.id}-xlsx`]
              ? <><RefreshCw size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> Generando...</>
              : <><FileSpreadsheet size={14} /> Excel</>}
          </button>

          <div style={{ flex: 1 }} />

          {/* Eliminar */}
          {!confirmDel ? (
            <button onClick={() => setConfirmDel(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--ink-3)', cursor: 'pointer' }}>
              <Trash2 size={13} /> Eliminar
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: 'var(--red)' }}>¿Eliminar este reporte?</span>
              <button onClick={onDelete} disabled={deleting === snap.id}
                style={{ padding: '6px 12px', background: 'var(--red)', color: 'var(--surface)', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {deleting === snap.id ? 'Eliminando...' : 'Confirmar'}
              </button>
              <button onClick={() => setConfirmDel(false)}
                style={{ padding: '6px 10px', background: 'none', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: 'var(--ink-2)' }}>
                Cancelar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
